import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { handleApiError } from '../../utils/errorHandler';
import Modal from '../Modal';
import {
  manualChargesApi,
  ManualChargeDto,
  BillingUpdateResultDto,
  formatCurrency,
} from '../../services/paymentsService';

// תואם ל-ManualChargeDescriptionMaxLength בשרת (ול-HasMaxLength ב-DbContext)
const DESCRIPTION_MAX_LENGTH = 200;

interface ManualChargeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (result: BillingUpdateResultDto) => void;
  clientId: number;
  charge?: ManualChargeDto | null; // עריכה אם קיים, אחרת הוספה
  vatRate?: number; // אחוז המע"מ, להצגת הסכום כולל מע"מ
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FieldsRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FormGroup = styled.div<{ $width?: string }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  ${props => (props.$width ? `width: ${props.$width}; max-width: 100%;` : '')}
`;

const Label = styled.label`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.9rem;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  padding: 0.75rem;
  border: 1px solid ${props => (props.$hasError ? 'var(--error-color)' : 'var(--border-color)')};
  border-radius: var(--border-radius-sm);
  font-size: 1rem;
  direction: rtl;
  text-align: right;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${props => (props.$hasError ? 'var(--error-color)' : 'var(--primary-color)')};
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const Hint = styled.span`
  color: var(--text-secondary);
  font-size: 0.8rem;
`;

const ErrorMessage = styled.span`
  color: var(--error-color);
  font-size: 0.8rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;

  ${props =>
    props.variant === 'primary'
      ? `background: var(--primary-color); color: #fff;
         &:hover:not(:disabled) { background: var(--primary-hover); }`
      : `background: var(--surface-color); color: var(--text-primary); border-color: var(--border-color);
         &:hover { background: var(--hover-background); }`}

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const toDateInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const todayInput = (): string => toDateInput(new Date().toISOString());

const ManualChargeFormModal: React.FC<ManualChargeFormModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  clientId,
  charge,
  vatRate,
}) => {
  const isEdit = !!charge;

  const [amount, setAmount] = useState('');
  const [chargeDate, setChargeDate] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // החיוב מוזן נטו, כמו העמלה, והמע"מ מתווסף אליו — מוצג כאן מה שהלקוח ישלם בפועל.
  const amountNumber = Number(amount);
  const grossAmount =
    vatRate != null && amount.trim() !== '' && !isNaN(amountNumber) && amountNumber > 0
      ? amountNumber * (1 + vatRate / 100)
      : null;

  // אתחול הטופס בכל פתיחה
  useEffect(() => {
    if (!isOpen) return;
    if (charge) {
      setAmount(String(charge.amount));
      setChargeDate(toDateInput(charge.chargeDate));
      setDescription(charge.description);
    } else {
      setAmount('');
      setChargeDate(todayInput());
      setDescription('');
    }
    setErrors({});
  }, [isOpen, charge]);

  const validateField = (name: string): string | null => {
    switch (name) {
      case 'amount': {
        if (!amount.trim()) return 'יש להזין סכום';
        const num = Number(amount);
        if (isNaN(num)) return 'הסכום אינו תקין';
        if (num <= 0) return 'הסכום חייב להיות גדול מאפס';
        if (Math.round(num * 100) / 100 !== num) return 'עד 2 ספרות אחרי הנקודה';
        return null;
      }
      case 'chargeDate':
        if (!chargeDate) return 'יש לבחור תאריך';
        return null;
      case 'description':
        if (!description.trim()) return 'יש להזין תיאור';
        if (description.trim().length > DESCRIPTION_MAX_LENGTH) return `עד ${DESCRIPTION_MAX_LENGTH} תווים`;
        return null;
      default:
        return null;
    }
  };

  const setFieldError = (name: string) => {
    const err = validateField(name);
    setErrors(prev => {
      const next = { ...prev };
      if (err) next[name] = err;
      else delete next[name];
      return next;
    });
  };

  const clearError = (name: string) => {
    setErrors(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validateAll = (): boolean => {
    const newErrors: Record<string, string> = {};
    ['amount', 'chargeDate', 'description'].forEach(f => {
      const err = validateField(f);
      if (err) newErrors[f] = err;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateAll()) return;

    const payload = {
      amount: Number(amount),
      chargeDate,
      description: description.trim(),
    };

    try {
      setSaving(true);
      const result = isEdit && charge
        ? await manualChargesApi.update(charge.id, payload)
        : await manualChargesApi.create({ clientId, ...payload });
      toast.success(isEdit ? 'החיוב עודכן בהצלחה' : 'החיוב נוסף בהצלחה');
      onSaved(result);
      onClose();
    } catch (err) {
      toast.error(handleApiError(err, 'אירעה שגיאה בשמירת החיוב'));
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
        ביטול
      </Button>
      <Button type="submit" form="manual-charge-form" variant="primary" disabled={saving}>
        {saving ? 'שומר...' : isEdit ? 'עדכון' : 'הוספה'}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'עריכת חיוב' : 'הוספת חיוב'}
      width="480px"
      footer={footer}
    >
      <Form id="manual-charge-form" onSubmit={handleSubmit}>
        <FieldsRow>
          <FormGroup $width="180px">
            <Label htmlFor="manual-charge-amount">סכום לפני מע"מ (₪)</Label>
            <Input
              id="manual-charge-amount"
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              value={amount}
              autoFocus
              $hasError={!!errors.amount}
              onChange={e => {
                setAmount(e.target.value);
                clearError('amount');
              }}
              onBlur={() => setFieldError('amount')}
              placeholder="0.00"
            />
            {errors.amount ? (
              <ErrorMessage>{errors.amount}</ErrorMessage>
            ) : grossAmount !== null ? (
              <Hint>כולל מע"מ: {formatCurrency(grossAmount)}</Hint>
            ) : (
              <Hint>המע"מ יתווסף לסכום</Hint>
            )}
          </FormGroup>

          <FormGroup $width="170px">
            <Label htmlFor="manual-charge-date">תאריך</Label>
            <Input
              id="manual-charge-date"
              type="date"
              value={chargeDate}
              $hasError={!!errors.chargeDate}
              onChange={e => {
                setChargeDate(e.target.value);
                clearError('chargeDate');
              }}
              onBlur={() => setFieldError('chargeDate')}
            />
            {errors.chargeDate && <ErrorMessage>{errors.chargeDate}</ErrorMessage>}
          </FormGroup>
        </FieldsRow>

        <FormGroup>
          <Label htmlFor="manual-charge-description">תיאור</Label>
          <Input
            id="manual-charge-description"
            type="text"
            value={description}
            maxLength={DESCRIPTION_MAX_LENGTH}
            $hasError={!!errors.description}
            onChange={e => {
              setDescription(e.target.value);
              clearError('description');
            }}
            onBlur={() => setFieldError('description')}
            placeholder="לדוגמה: פתיחת תיק, חוות דעת רפואית"
          />
          {errors.description ? (
            <ErrorMessage>{errors.description}</ErrorMessage>
          ) : (
            <Hint>החיוב מצטרף לעמלת הוועדה ונשמר גם אחרי אישורה</Hint>
          )}
        </FormGroup>
      </Form>
    </Modal>
  );
};

export default ManualChargeFormModal;
